import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuthHeaders } from "../auth/apiClient";

export interface AlertType {
  id: number;
  code: string;
  label: string;
  severity: "info" | "watch" | "critical";
  is_active: boolean;
}

export interface AlertRecipient {
  collaborator_id: number;
  collaborator_name?: string;
  channel?: "email" | "telegram" | "both";
  recipient_kind?: "to" | "cc" | "bcc";
}

export interface AlertPolicy {
  id: number;
  alert_type_id: number;
  alert_type_code: string;
  alert_type_label: string;
  route_id: number | null;
  route_code: string | null;
  route_name: string | null;
  channel: "email" | "telegram" | "both";
  recipients: AlertRecipient[];
  is_active: boolean;
}

async function fetchJson<T>(path: string, init: RequestInit): Promise<T> {
  const res = await fetch(path, init);
  if (!res.ok) throw new Error(`Failed to fetch ${path}: ${res.status}`);
  return res.json() as Promise<T>;
}

export function useAlertTypes() {
  const headers = useAuthHeaders();
  return useQuery({
    queryKey: ["settings", "alert-types"],
    queryFn: () => fetchJson<{ items: AlertType[] }>("/api/cockpit/settings/alert-types", { headers }),
  });
}

export function useAlertPolicies() {
  const headers = useAuthHeaders();
  return useQuery({
    queryKey: ["settings", "alert-policies"],
    queryFn: () => fetchJson<{ items: AlertPolicy[] }>("/api/cockpit/settings/alert-policies", { headers }),
  });
}

export function useAlertPolicyMutations() {
  const headers = useAuthHeaders(true);
  const qc = useQueryClient();
  return {
    save: useMutation({
      mutationFn: ({ id, payload }: { id?: number; payload: Partial<AlertPolicy> }) => fetchJson<AlertPolicy>(`/api/cockpit/settings/alert-policies${id ? `/${id}` : ""}`, {
        method: id ? "PATCH" : "POST",
        headers,
        body: JSON.stringify(payload),
      }),
      onSuccess: () => qc.invalidateQueries({ queryKey: ["settings", "alert-policies"] }),
    }),
  };
}
